import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Legend,
  ComposedChart
} from "recharts";
import { 
  ArrowLeft, 
  TrendingUp,
  Target,
  Wallet,
  PiggyBank,
  Loader2,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  Clock,
  AlertCircle,
  Lightbulb,
  DollarSign,
  Calendar,
  Sliders,
  RefreshCw,
  Briefcase,
  Gift,
  TrendingDown,
  Percent,
  Home,
  Car,
  GraduationCap,
  Plane,
  Heart,
  ShieldCheck,
  Share2,
  Copy,
  Twitter,
  Linkedin,
  Facebook
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectionPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Scenario state
  const [scenario, setScenario] = useState({
    income_change_percent: 0,
    expense_change_percent: 0,
    savings_boost_monthly: 0,
    one_time_windfall: 0
  });
  const [scenarioResult, setScenarioResult] = useState(null);
  const [calculatingScenario, setCalculatingScenario] = useState(false);
  
  // Goal-based scenario state
  const [goalTypes, setGoalTypes] = useState({});
  const [goalForm, setGoalForm] = useState({
    goal_type: "house",
    target_amount: 60000,
    current_savings_for_goal: 0,
    monthly_contribution: null
  });
  const [goalResult, setGoalResult] = useState(null);
  const [calculatingGoal, setCalculatingGoal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Goal type icons
  const goalIcons = {
    house: Home,
    car: Car,
    retirement: Briefcase,
    emergency_fund: ShieldCheck,
    vacation: Plane,
    education: GraduationCap,
    wedding: Heart,
    custom: Target
  };

  // Share goal progress functions
  const generateShareText = () => {
    if (!goalResult) return "";
    const { goal, current_plan, summary } = goalResult;
    const progressPercent = Math.round((goal.current_savings / goal.target_amount) * 100);
    
    return `🎯 I'm working toward my ${goal.name} goal with Tamba!\n\n` +
      `💰 Target: ${formatCurrency(goal.target_amount)}\n` +
      `📊 Progress: ${progressPercent}% saved\n` +
      `⏰ Timeline: ${current_plan.timeline?.months_to_goal || 'Calculating...'} months\n\n` +
      `Start your financial journey with Tamba! #FinancialGoals #WealthBuilding`;
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(generateShareText());
    window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
    toast.success("Opening Twitter...");
  };

  const shareOnLinkedIn = () => {
    const text = encodeURIComponent(generateShareText());
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}&summary=${text}`, '_blank');
    toast.success("Opening LinkedIn...");
  };

  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(generateShareText())}`, '_blank');
    toast.success("Opening Facebook...");
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateShareText());
      toast.success("Copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchProjection(userData.id);
  }, [navigate]);

  const fetchProjection = async (userId) => {
    setLoading(true);
    try {
      const [projResponse, goalsResponse] = await Promise.all([
        axios.get(`${API}/projection/${userId}`),
        axios.get(`${API}/projection/goal-types`)
      ]);
      setProjection(projResponse.data);
      setGoalTypes(goalsResponse.data.goal_types || {});
    } catch (error) {
      console.error("Error fetching projection:", error);
      toast.error("Failed to load projection");
    } finally {
      setLoading(false);
    }
  };

  const calculateGoalScenario = async () => {
    if (!user) return;
    
    setCalculatingGoal(true);
    try {
      const response = await axios.post(`${API}/projection/goal/${user.id}`, {
        goal_type: goalForm.goal_type,
        target_amount: goalForm.target_amount,
        current_savings_for_goal: goalForm.current_savings_for_goal,
        monthly_contribution: goalForm.monthly_contribution
      });
      setGoalResult(response.data);
      toast.success("Goal scenario calculated!");
    } catch (error) {
      console.error("Error calculating goal:", error);
      toast.error("Failed to calculate goal scenario");
    } finally {
      setCalculatingGoal(false);
    }
  };

  const resetGoalForm = () => {
    setGoalForm({
      goal_type: "house",
      target_amount: 60000,
      current_savings_for_goal: 0,
      monthly_contribution: null
    });
    setGoalResult(null);
  };

  const calculateScenario = async () => {
    if (!user) return;
    
    setCalculatingScenario(true);
    try {
      const response = await axios.post(`${API}/projection/scenario/${user.id}`, scenario);
      setScenarioResult(response.data);
    } catch (error) {
      console.error("Error calculating scenario:", error);
      toast.error("Failed to calculate scenario");
    } finally {
      setCalculatingScenario(false);
    }
  };

  const resetScenario = () => {
    setScenario({
      income_change_percent: 0,
      expense_change_percent: 0,
      savings_boost_monthly: 0,
      one_time_windfall: 0
    });
    setScenarioResult(null);
  };

  // Preset scenarios
  const presetScenarios = [
    { name: "10% Raise", icon: Briefcase, values: { income_change_percent: 10, expense_change_percent: 0, savings_boost_monthly: 0, one_time_windfall: 0 } },
    { name: "20% Raise", icon: TrendingUp, values: { income_change_percent: 20, expense_change_percent: 0, savings_boost_monthly: 0, one_time_windfall: 0 } },
    { name: "Cut Expenses 15%", icon: TrendingDown, values: { income_change_percent: 0, expense_change_percent: -15, savings_boost_monthly: 0, one_time_windfall: 0 } },
    { name: "$500/mo Extra Savings", icon: PiggyBank, values: { income_change_percent: 0, expense_change_percent: 0, savings_boost_monthly: 500, one_time_windfall: 0 } },
    { name: "$10K Bonus", icon: Gift, values: { income_change_percent: 0, expense_change_percent: 0, savings_boost_monthly: 0, one_time_windfall: 10000 } },
  ];

  const applyPreset = (preset) => {
    setScenario(preset.values);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-tamba-teal mx-auto mb-4" />
          <p className="text-muted-foreground">Calculating your financial future...</p>
        </div>
      </div>
    );
  }

  if (!projection) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">No Data Available</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Link a card and complete onboarding to see your financial projection
            </p>
            <Button onClick={() => navigate("/link-cards")} className="rounded-full">
              Link a Card
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { projection: proj, income_expense_summary, current_status, milestones, investment_goals } = projection;

  // Prepare comparison chart data if scenario exists
  const comparisonData = scenarioResult ? scenarioResult.baseline.projections.map((base, i) => ({
    month_name: base.month_name,
    baseline: base.projected_net_worth,
    scenario: scenarioResult.scenario.projections[i].projected_net_worth
  })) : [];

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-semibold">12-Month Projection</h1>
            <p className="text-xs text-muted-foreground">Your financial future at a glance</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Tabs for Overview and What If */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview" data-testid="overview-tab">
              <Calendar className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="whatif" data-testid="whatif-tab">
              <Sliders className="h-4 w-4 mr-2" />
              What If
            </TabsTrigger>
            <TabsTrigger value="goals" data-testid="goals-tab">
              <Target className="h-4 w-4 mr-2" />
              Goals
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
        {/* Hero Stats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <Card className="col-span-2 bg-gradient-to-br from-tamba-teal/10 to-tamba-blue/10 border-tamba-teal/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Projected Net Worth (12 mo)</p>
                  <p className="font-heading text-3xl font-bold">{formatCurrency(proj.projected_net_worth_12m)}</p>
                  <div className="flex items-center gap-1 mt-1 text-green-600">
                    <ArrowUpRight className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      +{formatCurrency(proj.total_growth)} ({proj.growth_percentage.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-tamba-teal/20 flex items-center justify-center">
                  <TrendingUp className="h-8 w-8 text-tamba-teal" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Current Net Worth</p>
                  <p className="font-semibold">{formatCurrency(proj.current_net_worth)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <PiggyBank className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Savings Rate</p>
                  <p className="font-semibold">{income_expense_summary.savings_rate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Net Worth Growth Chart */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-tamba-teal" />
                Net Worth Projection
              </CardTitle>
              <CardDescription>
                Based on your current income, expenses, and investment returns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={proj.monthly_data}>
                    <defs>
                      <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="month_name" 
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                    />
                    <YAxis 
                      tickFormatter={(value) => formatCurrencyShort(value)}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), ""]}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="projected_net_worth"
                      stroke="#14b8a6"
                      strokeWidth={3}
                      fill="url(#netWorthGradient)"
                      name="Net Worth"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Income vs Expenses */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="grid md:grid-cols-2 gap-4"
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">Monthly Cash Flow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Income</span>
                    <span className="font-medium text-green-600">+{formatCurrency(income_expense_summary.monthly_income)}</span>
                  </div>
                  <Progress value={100} className="h-2 bg-green-100" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Expenses</span>
                    <span className="font-medium text-red-500">-{formatCurrency(income_expense_summary.monthly_expenses)}</span>
                  </div>
                  <Progress 
                    value={(income_expense_summary.monthly_expenses / income_expense_summary.monthly_income) * 100} 
                    className="h-2 bg-red-100" 
                  />
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between">
                    <span className="font-medium">Monthly Savings</span>
                    <span className="font-bold text-tamba-teal">{formatCurrency(income_expense_summary.monthly_savings)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base">Growth Breakdown (12 mo)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={proj.monthly_data.filter((_, i) => i % 3 === 2 || i === 11)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month_name" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="cumulative_savings" name="Savings" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="investment_growth" name="Investment Growth" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Milestones */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-tamba-coral" />
                Financial Milestones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {milestones.length > 0 ? (
                  milestones.map((milestone, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        milestone.status === "achievable" 
                          ? "bg-green-100 text-green-600"
                          : "bg-amber-100 text-amber-600"
                      }`}>
                        {milestone.status === "achievable" ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <Clock className="h-5 w-5" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{milestone.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Target: {formatCurrency(milestone.target)}
                        </p>
                      </div>
                      <div className="text-right">
                        {milestone.month ? (
                          <span className="text-sm font-medium text-green-600">
                            Month {milestone.month}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            Beyond 12 months
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Add more financial data to see your milestones
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Investment Goals */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/5 to-transparent">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-tamba-yellow" />
                Personalized Investment Goals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {investment_goals.map((goal, index) => (
                  <div key={index} className="flex gap-3 p-3 bg-white/50 rounded-xl">
                    <Sparkles className="h-5 w-5 text-tamba-yellow flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">{goal.goal}</p>
                      <p className="text-xs text-muted-foreground mt-1">{goal.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Assumptions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-secondary/30">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Projection Assumptions:</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                <span>• {projection.assumptions.income_growth}</span>
                <span>• {projection.assumptions.expense_inflation}</span>
                <span>• {projection.assumptions.investment_return}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="flex flex-wrap gap-3"
        >
          <Button 
            onClick={() => setActiveTab("whatif")}
            className="rounded-full bg-purple-600 hover:bg-purple-700"
          >
            <Sliders className="h-4 w-4 mr-2" />
            Try What If Scenarios
          </Button>
          <Button 
            onClick={() => navigate("/budget")}
            variant="outline"
            className="rounded-full"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Optimize Budget
          </Button>
          <Button 
            onClick={() => navigate("/invest")}
            variant="outline"
            className="rounded-full"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Start Investing
          </Button>
        </motion.div>
          </TabsContent>

          {/* What If Tab */}
          <TabsContent value="whatif" className="mt-6 space-y-6">
            {/* Preset Scenarios */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    Quick Scenarios
                  </CardTitle>
                  <CardDescription>Click to apply a preset scenario</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {presetScenarios.map((preset, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => applyPreset(preset)}
                        data-testid={`preset-${index}`}
                      >
                        <preset.icon className="h-4 w-4 mr-1" />
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Custom Scenario Controls */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-transparent">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Sliders className="h-5 w-5 text-purple-600" />
                    Custom Scenario
                  </CardTitle>
                  <CardDescription>Adjust the sliders to see how changes affect your projection</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Income Change */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-green-600" />
                        Income Change
                      </Label>
                      <span className={`text-sm font-medium ${scenario.income_change_percent >= 0 ? "text-green-600" : "text-red-500"}`}>
                        {scenario.income_change_percent >= 0 ? "+" : ""}{scenario.income_change_percent}%
                      </span>
                    </div>
                    <Slider
                      value={[scenario.income_change_percent]}
                      onValueChange={(value) => setScenario(prev => ({ ...prev, income_change_percent: value[0] }))}
                      min={-50}
                      max={100}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>-50% (Job loss)</span>
                      <span>0%</span>
                      <span>+100% (Double)</span>
                    </div>
                  </div>

                  {/* Expense Change */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-tamba-coral" />
                        Expense Change
                      </Label>
                      <span className={`text-sm font-medium ${scenario.expense_change_percent <= 0 ? "text-green-600" : "text-red-500"}`}>
                        {scenario.expense_change_percent >= 0 ? "+" : ""}{scenario.expense_change_percent}%
                      </span>
                    </div>
                    <Slider
                      value={[scenario.expense_change_percent]}
                      onValueChange={(value) => setScenario(prev => ({ ...prev, expense_change_percent: value[0] }))}
                      min={-50}
                      max={50}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>-50% (Frugal)</span>
                      <span>0%</span>
                      <span>+50% (Lifestyle creep)</span>
                    </div>
                  </div>

                  {/* Extra Monthly Savings */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="flex items-center gap-2">
                        <PiggyBank className="h-4 w-4 text-tamba-teal" />
                        Extra Monthly Savings
                      </Label>
                      <span className="text-sm font-medium text-tamba-teal">
                        +{formatCurrency(scenario.savings_boost_monthly)}/mo
                      </span>
                    </div>
                    <Slider
                      value={[scenario.savings_boost_monthly]}
                      onValueChange={(value) => setScenario(prev => ({ ...prev, savings_boost_monthly: value[0] }))}
                      min={0}
                      max={2000}
                      step={100}
                      className="w-full"
                    />
                  </div>

                  {/* One-time Windfall */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label className="flex items-center gap-2">
                        <Gift className="h-4 w-4 text-amber-500" />
                        One-time Windfall
                      </Label>
                      <span className="text-sm font-medium text-amber-600">
                        {formatCurrency(scenario.one_time_windfall)}
                      </span>
                    </div>
                    <Slider
                      value={[scenario.one_time_windfall]}
                      onValueChange={(value) => setScenario(prev => ({ ...prev, one_time_windfall: value[0] }))}
                      min={0}
                      max={100000}
                      step={5000}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Bonus, tax refund, inheritance, etc.</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={calculateScenario}
                      disabled={calculatingScenario}
                      className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700"
                      data-testid="calculate-scenario-btn"
                    >
                      {calculatingScenario ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      Calculate Scenario
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetScenario}
                      className="rounded-full"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Scenario Results */}
            {scenarioResult && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                {/* Comparison Summary */}
                <Card className="border-green-200 bg-gradient-to-br from-green-50/50 to-transparent">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      Scenario Impact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">Current Projection</p>
                        <p className="font-semibold">{formatCurrency(scenarioResult.baseline.net_worth_12m)}</p>
                      </div>
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">With Scenario</p>
                        <p className="font-semibold text-purple-600">{formatCurrency(scenarioResult.scenario.net_worth_12m)}</p>
                      </div>
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">Difference</p>
                        <p className={`font-semibold flex items-center justify-center gap-1 ${scenarioResult.comparison.net_worth_difference >= 0 ? "text-green-600" : "text-red-500"}`}>
                          {scenarioResult.comparison.net_worth_difference >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                          {formatCurrency(Math.abs(scenarioResult.comparison.net_worth_difference))}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">Growth</p>
                        <p className={`font-semibold ${scenarioResult.comparison.difference_percent >= 0 ? "text-green-600" : "text-red-500"}`}>
                          {scenarioResult.comparison.difference_percent >= 0 ? "+" : ""}{scenarioResult.comparison.difference_percent.toFixed(1)}%
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-purple-100/50 rounded-xl">
                      <p className="text-sm">
                        <span className="font-medium">Monthly Impact:</span>{" "}
                        {scenarioResult.comparison.monthly_savings_impact >= 0 ? "+" : ""}
                        {formatCurrency(scenarioResult.comparison.monthly_savings_impact)}/month = {" "}
                        <span className="font-semibold">
                          {formatCurrency(scenarioResult.comparison.annual_extra_savings)}/year
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Comparison Chart */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="font-heading text-lg">Projection Comparison</CardTitle>
                    <CardDescription>Current vs. Scenario over 12 months</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={comparisonData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="baseline" 
                            name="Current Projection" 
                            stroke="#6b7280" 
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="scenario" 
                            name="With Scenario" 
                            stroke="#9333ea" 
                            strokeWidth={3}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Monthly Breakdown */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="font-heading text-base">Monthly Cash Flow Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-secondary/30 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-2 font-medium">Current</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Income</span>
                            <span>{formatCurrency(scenarioResult.baseline.monthly_income)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Expenses</span>
                            <span>-{formatCurrency(scenarioResult.baseline.monthly_expenses)}</span>
                          </div>
                          <div className="flex justify-between font-medium pt-2 border-t">
                            <span>Savings</span>
                            <span className="text-tamba-teal">{formatCurrency(scenarioResult.baseline.monthly_savings)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 bg-purple-100/30 rounded-xl border border-purple-200">
                        <p className="text-xs text-purple-600 mb-2 font-medium">With Scenario</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Income</span>
                            <span className={scenarioResult.scenario.monthly_income > scenarioResult.baseline.monthly_income ? "text-green-600" : ""}>
                              {formatCurrency(scenarioResult.scenario.monthly_income)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Expenses</span>
                            <span className={scenarioResult.scenario.monthly_expenses < scenarioResult.baseline.monthly_expenses ? "text-green-600" : ""}>
                              -{formatCurrency(scenarioResult.scenario.monthly_expenses)}
                            </span>
                          </div>
                          <div className="flex justify-between font-medium pt-2 border-t">
                            <span>Savings</span>
                            <span className="text-purple-600">{formatCurrency(scenarioResult.scenario.monthly_savings)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Help Text */}
            {!scenarioResult && (
              <Card className="bg-secondary/30">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Lightbulb className="h-5 w-5 text-tamba-yellow flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium mb-1">How to use What If scenarios</p>
                      <p className="text-xs text-muted-foreground">
                        Adjust the sliders to simulate different financial situations. Try exploring what happens if you 
                        get a raise, cut expenses, or receive a bonus. Click "Calculate Scenario" to see how it impacts 
                        your 12-month projection.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals" className="mt-6 space-y-6">
            {/* Goal Selection */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-tamba-teal/30 bg-gradient-to-br from-tamba-teal/5 to-transparent">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-tamba-teal" />
                    Set Your Financial Goal
                  </CardTitle>
                  <CardDescription>
                    Choose a goal and see when you can achieve it based on your current finances
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Goal Type Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">What's your goal?</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {Object.entries(goalTypes).map(([key, goal]) => {
                        const IconComponent = goalIcons[key] || Target;
                        const isSelected = goalForm.goal_type === key;
                        return (
                          <button
                            key={key}
                            onClick={() => {
                              setGoalForm(prev => ({
                                ...prev,
                                goal_type: key,
                                target_amount: goal.default_amount || 60000
                              }));
                              setGoalResult(null);
                            }}
                            className={`p-3 rounded-xl border-2 transition-all text-left ${
                              isSelected
                                ? "border-tamba-teal bg-tamba-teal/10"
                                : "border-border hover:border-tamba-teal/50 hover:bg-secondary/50"
                            }`}
                            data-testid={`goal-type-${key}`}
                          >
                            <IconComponent className={`h-5 w-5 mb-2 ${isSelected ? "text-tamba-teal" : "text-muted-foreground"}`} />
                            <p className={`text-sm font-medium ${isSelected ? "text-tamba-teal" : ""}`}>{goal.name}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Target Amount */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      Target Amount
                    </Label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        value={goalForm.target_amount}
                        onChange={(e) => setGoalForm(prev => ({ ...prev, target_amount: Number(e.target.value) }))}
                        className="flex-1"
                        placeholder="60000"
                        data-testid="goal-target-amount"
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatCurrency(goalForm.target_amount)}
                      </span>
                    </div>
                    {goalTypes[goalForm.goal_type]?.description && (
                      <p className="text-xs text-muted-foreground">{goalTypes[goalForm.goal_type].description}</p>
                    )}
                  </div>

                  {/* Current Savings for Goal */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <PiggyBank className="h-4 w-4 text-tamba-teal" />
                      Already Saved Toward This Goal
                    </Label>
                    <Slider
                      value={[goalForm.current_savings_for_goal]}
                      onValueChange={(value) => setGoalForm(prev => ({ ...prev, current_savings_for_goal: value[0] }))}
                      min={0}
                      max={goalForm.target_amount * 0.8}
                      step={500}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>$0</span>
                      <span className="font-medium text-tamba-teal">{formatCurrency(goalForm.current_savings_for_goal)}</span>
                      <span>{formatCurrency(goalForm.target_amount * 0.8)}</span>
                    </div>
                  </div>

                  {/* Monthly Contribution Override */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      Monthly Contribution (optional)
                    </Label>
                    <Input
                      type="number"
                      value={goalForm.monthly_contribution || ""}
                      onChange={(e) => setGoalForm(prev => ({ ...prev, monthly_contribution: e.target.value ? Number(e.target.value) : null }))}
                      className="flex-1"
                      placeholder="Leave blank to auto-calculate from savings"
                      data-testid="goal-monthly-contribution"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank to use 50% of your available monthly savings
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={calculateGoalScenario}
                      disabled={calculatingGoal}
                      className="flex-1 rounded-full bg-tamba-teal hover:bg-tamba-teal/90"
                      data-testid="calculate-goal-btn"
                    >
                      {calculatingGoal ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      Calculate Timeline
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetGoalForm}
                      className="rounded-full"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Goal Results */}
            {goalResult && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                {/* Summary Card */}
                <Card className={`border-2 ${goalResult.summary.feasibility === "Achievable" ? "border-green-200 bg-gradient-to-br from-green-50/50" : "border-amber-200 bg-gradient-to-br from-amber-50/50"}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      {goalResult.summary.feasibility === "Achievable" ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                      )}
                      {goalResult.goal.name}
                    </CardTitle>
                    <CardDescription>{goalResult.summary.message}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">Target</p>
                        <p className="font-semibold">{formatCurrency(goalResult.goal.target_amount)}</p>
                      </div>
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">Already Saved</p>
                        <p className="font-semibold text-tamba-teal">{formatCurrency(goalResult.goal.current_savings)}</p>
                      </div>
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                        <p className="font-semibold text-amber-600">{formatCurrency(goalResult.goal.remaining)}</p>
                      </div>
                      <div className="text-center p-3 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1">Monthly Savings</p>
                        <p className="font-semibold">{formatCurrency(goalResult.current_plan.monthly_contribution)}/mo</p>
                      </div>
                    </div>

                    {/* Timeline Info */}
                    {goalResult.current_plan.timeline && (
                      <div className="mt-4 p-4 bg-tamba-teal/10 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Clock className="h-5 w-5 text-tamba-teal" />
                          <div>
                            <p className="font-medium">
                              {goalResult.current_plan.timeline.achievable ? (
                                <>Achievable in <span className="text-tamba-teal font-bold">{goalResult.current_plan.timeline.months_to_goal} months</span></>
                              ) : (
                                "Timeline exceeds 10 years"
                              )}
                            </p>
                            {goalResult.current_plan.timeline.target_date && (
                              <p className="text-sm text-muted-foreground">
                                Target date: {goalResult.current_plan.timeline.target_date}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Share Progress Button */}
                    <div className="mt-4 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setShowShareModal(true)}
                        className="w-full rounded-full"
                        data-testid="share-goal-btn"
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share My Progress
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Share Modal */}
                {showShareModal && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => setShowShareModal(false)}
                  >
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h3 className="font-heading text-xl font-semibold mb-2">Share Your Goal Progress</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Celebrate your financial journey and inspire others!
                      </p>
                      
                      {/* Preview */}
                      <div className="bg-secondary/30 rounded-xl p-4 mb-4 text-sm">
                        <p className="whitespace-pre-line">{generateShareText()}</p>
                      </div>

                      {/* Share Buttons */}
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          variant="outline"
                          onClick={shareOnTwitter}
                          className="rounded-full"
                          data-testid="share-twitter"
                        >
                          <Twitter className="h-4 w-4 mr-2" />
                          Twitter
                        </Button>
                        <Button
                          variant="outline"
                          onClick={shareOnLinkedIn}
                          className="rounded-full"
                          data-testid="share-linkedin"
                        >
                          <Linkedin className="h-4 w-4 mr-2" />
                          LinkedIn
                        </Button>
                        <Button
                          variant="outline"
                          onClick={shareOnFacebook}
                          className="rounded-full"
                          data-testid="share-facebook"
                        >
                          <Facebook className="h-4 w-4 mr-2" />
                          Facebook
                        </Button>
                        <Button
                          variant="outline"
                          onClick={copyToClipboard}
                          className="rounded-full"
                          data-testid="share-copy"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Text
                        </Button>
                      </div>

                      <Button
                        variant="ghost"
                        onClick={() => setShowShareModal(false)}
                        className="w-full mt-4 rounded-full"
                      >
                        Close
                      </Button>
                    </motion.div>
                  </motion.div>
                )}

                {/* Projection Chart */}
                {goalResult.projections && goalResult.projections.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="font-heading text-lg">Savings Projection</CardTitle>
                      <CardDescription>See your progress toward {formatCurrency(goalResult.goal.target_amount)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={goalResult.projections}>
                            <defs>
                              <linearGradient id="goalGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) => `M${v}`}
                            />
                            <YAxis 
                              tickFormatter={formatCurrencyShort}
                              tick={{ fontSize: 11 }}
                            />
                            <Tooltip 
                              formatter={(value) => [formatCurrency(value), "Balance"]}
                              labelFormatter={(label) => `Month ${label}`}
                            />
                            <Area
                              type="monotone"
                              dataKey="balance"
                              stroke="#14b8a6"
                              strokeWidth={2}
                              fill="url(#goalGradient)"
                            />
                            {/* Target line */}
                            <Line
                              type="monotone"
                              dataKey={() => goalResult.goal.target_amount}
                              stroke="#f97316"
                              strokeDasharray="5 5"
                              strokeWidth={2}
                              dot={false}
                              name="Target"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Alternative Scenarios */}
                {goalResult.alternative_scenarios && goalResult.alternative_scenarios.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                        Speed Up Your Goal
                      </CardTitle>
                      <CardDescription>See how increasing your savings affects your timeline</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-3 gap-3">
                        {goalResult.alternative_scenarios.map((scenario, index) => (
                          <div key={index} className="p-4 bg-purple-50/50 rounded-xl border border-purple-100">
                            <p className="text-sm font-medium text-purple-700 mb-2">{scenario.name}</p>
                            <p className="text-xs text-muted-foreground mb-1">
                              {formatCurrency(scenario.monthly_contribution)}/month
                            </p>
                            <p className="font-semibold text-tamba-teal">
                              {scenario.months_to_goal} months
                            </p>
                            {scenario.target_date && (
                              <p className="text-xs text-muted-foreground mt-1">{scenario.target_date}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {goalResult.recommendations && goalResult.recommendations.length > 0 && (
                  <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/5 to-transparent">
                    <CardHeader className="pb-3">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-tamba-yellow" />
                        Personalized Tips
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {goalResult.recommendations.map((rec, index) => (
                          <div key={index} className="flex gap-3 p-3 bg-white/50 rounded-xl">
                            <CheckCircle2 className="h-5 w-5 text-tamba-teal flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium">{typeof rec === 'object' ? rec.title : rec}</p>
                              {typeof rec === 'object' && rec.description && (
                                <p className="text-muted-foreground text-xs mt-1">{rec.description}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}

            {/* Help Text when no result */}
            {!goalResult && (
              <Card className="bg-secondary/30">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Lightbulb className="h-5 w-5 text-tamba-yellow flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium mb-1">How Goal Planning Works</p>
                      <p className="text-xs text-muted-foreground">
                        Select a financial goal like buying a house or retirement. Enter your target amount and any 
                        existing savings. We'll calculate how long it will take based on your income and spending 
                        patterns, and show you ways to reach your goal faster.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ProjectionPage;
